import React, { useEffect, useReducer, useRef } from 'react';
import {
  Button, ButtonToolbar, Col, Form, FormInstance, Grid, Row, Schema, SelectPicker, Stack, Toggle,
} from 'rsuite';
import { ItemDataType } from 'rsuite/CascadeTree';
import { usePartnerSetupContext } from './PartnerSetupProvider';
import PartnerDeactivationAlert from './PartnerDeactivationAlert';
import B2bAddressEdit, { B2bAddressEditRef } from '../Address/B2bAddressEdit';
import PartnerSetupGlobalStockSwitch from './PartnerSetupGlobalStockSwitch';
import { WorkflowStatus } from '../../api/partner/partnerApiTypes';
import { useStoreState } from '../../store/hooks';
import { Permission } from '../../store/user/types';
import { showErrorMessage } from '../../utils/message';

const WORKFLOW_STATUSES: ItemDataType[] = Object
  .values(WorkflowStatus)
  .map(workflowStatus => ({ label: workflowStatus, value: workflowStatus }));

interface Partner {
  partnerCode: string;
  partnerName: string;
  workflowStatus: WorkflowStatus;
  b2bSellModeEnabled: boolean;
}

interface Edit {
  partnerName: string;
  workflowStatus: WorkflowStatus;
  b2bSellModeEnabled: boolean;
}

type Status = 'ready' | 'saving';

interface State {
  status: Status;
  edit: Edit;
  isDeactivationAlertOpen: boolean;
}

const model = Schema.Model({
  partnerName: Schema.Types.StringType().isRequired('Partner name is required.'),
  workflowStatus: Schema.Types.StringType().isRequired('Workflow status is required.'),
});

function toEdit({ partnerName, workflowStatus, b2bSellModeEnabled }: Partner): Edit {
  return { partnerName, workflowStatus, b2bSellModeEnabled };
}

function getInitialState(partner: Partner): State {
  return {
    status: 'ready',
    edit: toEdit(partner),
    isDeactivationAlertOpen: false,
  };
}

type StatusSetAction = { type: 'status/set', payload: Status };
type EditSetAction = { type: 'edit/set', payload: Edit };
type DeactivationAlertSetAction = { type: 'deactivationAlert/set', payload: boolean };
type Action = StatusSetAction | EditSetAction | DeactivationAlertSetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'status/set':
      return { ...state, status: action.payload };
    case 'edit/set':
      return { ...state, edit: action.payload };
    case 'deactivationAlert/set':
      return { ...state, isDeactivationAlertOpen: action.payload };
    default:
      return state;
  }
}

export default function PartnerSetupMpi() {
  const { partner, savePartner } = usePartnerSetupContext();
  const { hasPermission } = useStoreState(storeState => storeState.user);
  const [state, dispatch] = useReducer(reducer, getInitialState(partner));
  const formRef = useRef<FormInstance>(null);
  const b2bBuyerAddressEditRef = useRef<B2bAddressEditRef>(null);
  const b2bSupplierAddressEditRef = useRef<B2bAddressEditRef>(null);

  const canEditWorkflowStatus = hasPermission(Permission.PartnerSetupWorkflowStatus);

  useEffect(() => {
    const { edit } = getInitialState(partner);

    dispatch({ type: 'edit/set', payload: edit });
  }, [partner]);

  function handleChange(edit: Edit) {
    dispatch({ type: 'edit/set', payload: edit });
  }

  function areB2bAddressesValid(): boolean {
    const buyerValid = !!b2bBuyerAddressEditRef.current?.isValid();
    const supplierValid = !!b2bSupplierAddressEditRef.current?.isValid();

    return buyerValid && supplierValid;
  }

  function isBeingDeactivated(): boolean {
    return state.edit.workflowStatus === WorkflowStatus.DEACTIVATED
      && partner.workflowStatus !== WorkflowStatus.DEACTIVATED;
  }

  function isEditValid(): boolean {
    if (!formRef.current?.check()) {
      return false;
    }

    if (!areB2bAddressesValid() && state.edit.b2bSellModeEnabled) {
      showErrorMessage('Please fill B2B buyer and supplier addresses before enabling B2B.');
      return false;
    }

    return true;
  }

  async function submitPartner(): Promise<void> {
    dispatch({ type: 'status/set', payload: 'saving' });

    try {
      await savePartner({ ...partner, ...state.edit });
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  function handleSubmit() {
    if (state.status === 'saving') {
      return;
    }

    if (!isEditValid()) {
      return;
    }

    if (isBeingDeactivated()) {
      dispatch({ type: 'deactivationAlert/set', payload: true });
      return;
    }

    submitPartner().then();
  }

  function handleDeactivationConfirm() {
    if (state.status === 'saving') {
      return;
    }

    submitPartner().then(() => dispatch({ type: 'deactivationAlert/set', payload: false }));
  }

  function handleDeactivationClose() {
    if (state.status === 'saving') {
      return;
    }

    dispatch({ type: 'deactivationAlert/set', payload: false });
  }

  function handleCancel() {
    const { edit } = getInitialState(partner);

    dispatch({ type: 'edit/set', payload: edit });
  }

  return (
    <>
      <Stack spacing={20} direction="column" alignItems="flex-start">
        <div>
          <Form
            ref={formRef}
            model={model}
            layout="horizontal"
            formValue={state.edit}
            onChange={(edit) => handleChange(edit as Edit)}
          >
            <Form.Group controlId="partnerCode">
              <Form.ControlLabel>Partner code</Form.ControlLabel>
              <Form.Control name="partnerCode" value={partner.partnerCode} readOnly />
            </Form.Group>
            <Form.Group controlId="partnerName">
              <Form.ControlLabel>Partner name</Form.ControlLabel>
              <Form.Control name="partnerName" />
            </Form.Group>
            {
              canEditWorkflowStatus && (
                <Form.Group controlId="workflowStatus">
                  <Form.ControlLabel>Workflow status</Form.ControlLabel>
                  <Form.Control
                    name="workflowStatus"
                    accepter={SelectPicker}
                    data={WORKFLOW_STATUSES}
                    cleanable={false}
                    searchable={false}
                  />
                </Form.Group>
              )
            }
            <Form.Group controlId="b2bSellModeEnabled">
              <Form.ControlLabel>B2B Sell Mode</Form.ControlLabel>
              <Form.Control
                checked={state.edit.b2bSellModeEnabled}
                name="b2bSellModeEnabled"
                accepter={Toggle}
              />
            </Form.Group>
            <Form.Group>
              <ButtonToolbar>
                <Button
                  appearance="primary"
                  onClick={() => handleSubmit()}
                  loading={state.status === 'saving'}
                >
                  Submit
                </Button>
                <Button
                  appearance="ghost"
                  onClick={() => handleCancel()}
                >
                  Cancel
                </Button>
              </ButtonToolbar>
            </Form.Group>
          </Form>
        </div>
        {
          state.edit.b2bSellModeEnabled && (
            <Grid fluid>
              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <h4>B2B address buyer </h4>
                  <B2bAddressEdit ref={b2bBuyerAddressEditRef} addressType="BUYER" />
                </Col>
                <Col xs={24} md={12}>
                  <h4>B2B address supplier</h4>
                  <B2bAddressEdit ref={b2bSupplierAddressEditRef} addressType="SUPPLIER" />
                </Col>
              </Row>
            </Grid>
          )
        }
        <div>
          <h4>Stock</h4>
          <PartnerSetupGlobalStockSwitch />
        </div>
      </Stack>
      <PartnerDeactivationAlert
        isOpen={state.isDeactivationAlertOpen}
        isLoading={state.status === 'saving'}
        onConfirm={() => handleDeactivationConfirm()}
        onClose={() => handleDeactivationClose()}
      />
    </>
  );
}
